/**
 * Dashboard ThemeProvider — wraps `@sill/ui`'s `createThemeContext()` with
 * the actual provider implementation, localStorage persistence, and the
 * `document.documentElement.dataset.theme` reflection that activates the CSS
 * variable set published by `@sill/ui/css/theme`.
 *
 * Persistence key (per ADR-0003): `sill.dashboard.theme`.
 *
 * Default theme: `'light'` per founder decision 2026-10-04. Dark remains
 * available via the user toggle. We do NOT consult `prefers-color-scheme`
 * — the founder's directive is "no stored pref → light", not "match the
 * OS".
 *
 * ## One-time migration (2026-10-05)
 *
 * The pre-flip code wrote the resolved theme to localStorage on every
 * mount, so virtually every existing visitor has `'dark'` persisted even
 * though they never chose it. Shipping the light default without a
 * migration would leave those visitors stuck on dark forever.
 *
 * Migration marker: `sill.dashboard.theme.v = '2'`.
 *
 *   - Marker ABSENT: treat the stored value as unchosen. Delete it, write
 *     the marker, and resolve to `DEFAULT_THEME` (light). The user's
 *     next explicit toggle persists normally.
 *   - Marker PRESENT: honour the stored value as today.
 *
 * The pre-paint inline script in `index.html` runs the same check so there
 * is no flash of dark on first load after this ships.
 *
 * Also from 2026-10-05: the mount effect no longer writes the theme. Only
 * an explicit toggle persists. That way a future default change isn't
 * frozen into every visitor's localStorage by the next page load.
 *
 * The shared `@sill/ui` package default stays `'dark'` so the marketing
 * app's own ThemeProvider is unaffected. The default here is a per-app
 * parameter of this file, not a change to the shared package.
 *
 * Storage reads return `unknown`; we narrow it before trusting the value.
 * This is the one place the `noUncheckedIndexedAccess` / parse-boundary
 * `unknown` escape hatch is acceptable per the ADR.
 */

import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { darkC, lightC } from '@sill/ui';
import type { ThemeCtx, ThemeName, ThemeTokens } from '@sill/ui';

const STORAGE_KEY = 'sill.dashboard.theme';
/**
 * Migration marker key. Bumping the value re-runs the reset once per
 * visitor. Keep in sync with the pre-paint script in `index.html`.
 */
const MIGRATION_KEY = 'sill.dashboard.theme.v';
const MIGRATION_VERSION = '2';

/** Per-app default — see module header. */
const DEFAULT_THEME: ThemeName = 'light';

const ThemeReactCtx = createContext<ThemeCtx>({
  C: lightC,
  theme: 'light',
  setTheme: () => {
    /* no-op default */
  },
});

/**
 * Read + migrate the stored preference. Idempotent after the first call
 * per visitor. Visible for tests.
 *
 * If the migration marker is absent, we treat any stored value as the
 * pre-flip auto-persisted leftover and drop it (returning `null` so the
 * caller falls through to `DEFAULT_THEME`). After the first call the
 * marker is set and subsequent reads honour the stored value as today.
 *
 * Storage-disabled / private mode: returns `null`, does not throw.
 */
export function readStoredTheme(): ThemeName | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const marker: unknown = window.localStorage.getItem(MIGRATION_KEY);
    if (marker !== MIGRATION_VERSION) {
      // One-time reset: the stored value (if any) was auto-persisted on
      // mount under the old default and never reflects a real choice.
      try {
        window.localStorage.removeItem(STORAGE_KEY);
      } catch {
        // Ignore — storage is read-only or quota-exceeded; just skip.
      }
      try {
        window.localStorage.setItem(MIGRATION_KEY, MIGRATION_VERSION);
      } catch {
        // Marker couldn't persist (storage disabled). Returning `null`
        // still resolves to the light default; the migration will simply
        // re-run on the next load — which is harmless, since there is
        // nothing stored to reset in that case either.
      }
      return null;
    }
    const raw: unknown = window.localStorage.getItem(STORAGE_KEY);
    if (raw === 'dark' || raw === 'light') {
      return raw;
    }
  } catch {
    // Storage may be disabled (incognito + cookies blocked). Treat as no
    // preference and fall through to the default.
  }
  return null;
}

function writeStoredTheme(name: ThemeName): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, name);
    // Writing the marker alongside an explicit choice means a toggle
    // ALSO completes the migration if the user clicks before the mount
    // effect has run on another tab.
    window.localStorage.setItem(MIGRATION_KEY, MIGRATION_VERSION);
  } catch {
    // See readStoredTheme.
  }
}

function applyDocumentTheme(name: ThemeName): void {
  if (typeof document === 'undefined') {
    return;
  }
  document.documentElement.dataset['theme'] = name;
}

export interface ThemeProviderProps {
  children: ReactNode;
  /** Optional initial theme — useful for tests; production reads localStorage. */
  initial?: ThemeName;
}

export function ThemeProvider({ children, initial }: ThemeProviderProps): JSX.Element {
  const [theme, setThemeState] = useState<ThemeName>(() => {
    if (initial) return initial;
    return readStoredTheme() ?? DEFAULT_THEME;
  });

  useEffect(() => {
    // Reflect the active theme into the DOM for the CSS variable set.
    // Deliberately do NOT persist here — only an explicit toggle writes
    // (see setTheme below). That keeps future default flips honest.
    applyDocumentTheme(theme);
  }, [theme]);

  const value = useMemo<ThemeCtx>(() => {
    const C: ThemeTokens = theme === 'dark' ? darkC : lightC;
    return {
      C,
      theme,
      setTheme: (next) => {
        setThemeState(next);
        writeStoredTheme(next);
      },
    };
  }, [theme]);

  return <ThemeReactCtx.Provider value={value}>{children}</ThemeReactCtx.Provider>;
}

export function useTheme(): ThemeCtx {
  return useContext(ThemeReactCtx);
}

export function useC(): ThemeTokens {
  return useContext(ThemeReactCtx).C;
}
